import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const DARK_BLUE = '#142D55';
const BLUE      = '#3E68B2';
const GREEN     = '#38D430';
const BORDER    = '#CBD5E0';

const TROY_OZ_PER_KG = 32.1507;

const COUNTRIES = [
  'Argentina','Australia','Austria','Belgium','Bolivia','Brazil','Canada',
  'Chile','China','Colombia','Czech Republic','Denmark','Ecuador','Egypt',
  'Finland','France','Germany','Ghana','Greece','Hungary','India','Indonesia',
  'Ireland','Italy','Japan','Kazakhstan','Malaysia','Mexico','Morocco',
  'Netherlands','New Zealand','Norway','Peru','Philippines','Poland',
  'Portugal','Romania','Russia','Saudi Arabia','Singapore','South Africa',
  'South Korea','Spain','Sweden','Switzerland','Taiwan','Thailand','Turkey',
  'UAE','United Kingdom','United States','Vietnam','Zimbabwe','Other',
];

const COUNTRY_BENCHMARKS = {
  Argentina:        { electricity: 0.06, labour: 24960  },
  Australia:        { electricity: 0.10, labour: 79040  },
  Austria:          { electricity: 0.14, labour: 62400  },
  Belgium:          { electricity: 0.16, labour: 66560  },
  Bolivia:          { electricity: 0.06, labour: 8320   },
  Brazil:           { electricity: 0.12, labour: 16640  },
  Canada:           { electricity: 0.08, labour: 62400  },
  Chile:            { electricity: 0.11, labour: 24960  },
  China:            { electricity: 0.08, labour: 16640  },
  Colombia:         { electricity: 0.09, labour: 10400  },
  'Czech Republic': { electricity: 0.13, labour: 31200  },
  Denmark:          { electricity: 0.18, labour: 72800  },
  Ecuador:          { electricity: 0.07, labour: 8320   },
  Egypt:            { electricity: 0.04, labour: 6240   },
  Finland:          { electricity: 0.12, labour: 66560  },
  France:           { electricity: 0.13, labour: 58240  },
  Germany:          { electricity: 0.22, labour: 66560  },
  Ghana:            { electricity: 0.08, labour: 6240   },
  Greece:           { electricity: 0.14, labour: 37440  },
  Hungary:          { electricity: 0.12, labour: 24960  },
  India:            { electricity: 0.09, labour: 8320   },
  Indonesia:        { electricity: 0.08, labour: 8320   },
  Ireland:          { electricity: 0.19, labour: 58240  },
  Italy:            { electricity: 0.17, labour: 49920  },
  Japan:            { electricity: 0.17, labour: 52000  },
  Kazakhstan:       { electricity: 0.05, labour: 12480  },
  Malaysia:         { electricity: 0.06, labour: 12480  },
  Mexico:           { electricity: 0.09, labour: 12480  },
  Morocco:          { electricity: 0.11, labour: 8320   },
  Netherlands:      { electricity: 0.16, labour: 62400  },
  'New Zealand':    { electricity: 0.10, labour: 58240  },
  Norway:           { electricity: 0.06, labour: 83200  },
  Peru:             { electricity: 0.07, labour: 10400  },
  Philippines:      { electricity: 0.14, labour: 8320   },
  Poland:           { electricity: 0.14, labour: 24960  },
  Portugal:         { electricity: 0.13, labour: 33280  },
  Romania:          { electricity: 0.11, labour: 20800  },
  Russia:           { electricity: 0.05, labour: 16640  },
  'Saudi Arabia':   { electricity: 0.05, labour: 24960  },
  Singapore:        { electricity: 0.14, labour: 45760  },
  'South Africa':   { electricity: 0.08, labour: 14560  },
  'South Korea':    { electricity: 0.10, labour: 37440  },
  Spain:            { electricity: 0.14, labour: 41600  },
  Sweden:           { electricity: 0.09, labour: 66560  },
  Switzerland:      { electricity: 0.14, labour: 83200  },
  Taiwan:           { electricity: 0.09, labour: 31200  },
  Thailand:         { electricity: 0.10, labour: 14560  },
  Turkey:           { electricity: 0.11, labour: 16640  },
  UAE:              { electricity: 0.08, labour: 24960  },
  'United Kingdom': { electricity: 0.24, labour: 52000  },
  'United States':  { electricity: 0.09, labour: 62400  },
  Vietnam:          { electricity: 0.08, labour: 8320   },
  Zimbabwe:         { electricity: 0.09, labour: 6240   },
  Other:            { electricity: 0.10, labour: 46800  },
};

// ── UI primitives ────────────────────────────────────────────────────────────
function BlockHeader({ number, title, subtitle }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        <span style={{ background: BLUE, color: '#fff', borderRadius: '50%', width: 26, height: 26,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{number}</span>
        <span style={{ fontWeight: 700, fontSize: '1rem', color: DARK_BLUE }}>{title}</span>
      </div>
      {subtitle && (
        <p style={{ marginLeft: 38, marginTop: '0.25rem', fontSize: '0.8125rem', color: '#718096' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function SubLabel({ children }) {
  return (
    <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096',
      textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.625rem' }}>
      {children}
    </p>
  );
}

function Field({ label, required, unit, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#4A5568',
        textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
        {required && <span style={{ color: '#E53E3E', marginLeft: 2 }}>*</span>}
        {unit && <span style={{ marginLeft: 5, fontWeight: 400, textTransform: 'none', color: '#718096' }}>({unit})</span>}
      </label>
      {children}
      {hint && <span style={{ fontSize: '0.6875rem', color: '#718096', marginTop: 2 }}>{hint}</span>}
    </div>
  );
}

function Num({ value, onChange, placeholder, min, max, step = 'any', optional, badge, unit, readOnly }) {
  const [focused, setFocused] = useState(false);
  const hasBadge = !!badge;
  const hasUnit  = !!unit;
  return (
    <div style={{ position: 'relative' }}>
      <input type="number" value={value}
        onChange={e => !readOnly && onChange && onChange(e.target.value)}
        placeholder={optional ? 'Optional' : placeholder}
        min={min} max={max} step={step} readOnly={readOnly}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: (hasBadge || hasUnit) ? '0.5rem 4.5rem 0.5rem 0.75rem' : '0.5rem 0.75rem',
          border: `1.5px solid ${focused && !readOnly ? BLUE : BORDER}`,
          borderRadius: 6, fontSize: '0.9375rem',
          color: readOnly ? '#718096' : DARK_BLUE,
          background: readOnly ? '#F7F8FA' : '#fff',
          outline: 'none', transition: 'border-color 0.15s',
          cursor: readOnly ? 'default' : undefined,
        }}
      />
      {hasUnit && !hasBadge && (
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: '0.78rem', color: '#718096', pointerEvents: 'none' }}>{unit}</span>
      )}
      {hasBadge && (
        <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
          fontSize: '0.6rem', fontWeight: 700, padding: '2px 5px', borderRadius: 3,
          background: badge === 'live' ? '#C6F6D5' : '#FEF3C7',
          color: badge === 'live' ? '#276749' : '#92400E' }}>
          {badge === 'live' ? 'LIVE' : 'EST.'}
        </span>
      )}
    </div>
  );
}

function Sel({ value, onChange, options, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '0.5rem 2rem 0.5rem 0.75rem',
        border: `1.5px solid ${focused ? BLUE : BORDER}`,
        borderRadius: 6, fontSize: '0.9375rem', color: value ? DARK_BLUE : '#718096',
        background: '#fff', outline: 'none', cursor: 'pointer', appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23718096' strokeWidth='1.5' fill='none' strokeLinecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center',
      }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

function Txt({ value, onChange, placeholder, readOnly }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type="text" value={value}
      onChange={e => !readOnly && onChange && onChange(e.target.value)}
      placeholder={placeholder} readOnly={readOnly}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{
        width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem',
        border: `1.5px solid ${focused && !readOnly ? BLUE : BORDER}`,
        borderRadius: 6, fontSize: '0.9375rem',
        color: readOnly ? '#718096' : DARK_BLUE,
        background: readOnly ? '#F7F8FA' : '#fff',
        outline: 'none', transition: 'border-color 0.15s',
        cursor: readOnly ? 'default' : undefined,
      }}
    />
  );
}

function TwoCol({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>{children}</div>;
}
function ThreeCol({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>{children}</div>;
}
function Block({ children, style }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10,
      padding: '1.5rem', marginBottom: '1.25rem', ...style }}>
      {children}
    </div>
  );
}
function Divider() {
  return <div style={{ borderTop: '1px solid #EDF2F7', margin: '1.125rem 0' }} />;
}

// ── Default state ────────────────────────────────────────────────────────────
const INIT = {
  // Customer
  company_name: '', contact_person: '', country: '',
  // Block 1 — Feed & Production
  net_ag_output_kg_d:  600,
  existing_process:    'Moebius electrorefining',
  feed_ag_pct:         90,
  feed_cu_pct:         10,
  feed_au_ppm:         50,
  feed_pdpt_ppm:       10,
  // Block 2 — Financial & Operational
  silver_price_usd_kg:          1903,   // USD/kg (~$59.25/tr oz)
  gold_price_usd_troy_oz:       3350,
  copper_price_usd_kg:          9.8,
  palladium_price_usd_troy_oz:  1050,
  platinum_price_usd_troy_oz:   1000,
  silver_lease_rate:            0.10,   // stored as decimal (0.10 = 10%/yr)
  power_price_usd_kwh:          0.10,
  labour_cost_usd_yr:           46800,
  operating_days:               330,
  target_payback_years:         2,
  hno3_price_usd_kg:            0.77,
  naoh_price_usd_kg:            0.50,
  hcl_price_usd_l:              0.15,
  zinc_dust_usd_kg:             2.50,
  sludge_disposal_usd_t:        300,
  // Block 3 — Process Benchmarks
  // Moebius
  moebius_ag_inventory_factor:  6.6,
  moebius_hno3_kg_per_kg_ag:    1.5,
  moebius_hcl_l_per_kg_ag:      0.5,
  moebius_naoh_kg_per_kg_ag:    0.8,
  moebius_zn_dust_kg_per_kg_ag: 0.0,
  moebius_kwh_per_kg_ag:        1.5,
  moebius_operators:             4,
  // emew
  emew_ag_inventory_multiple:   2.5,
  emew_hno3_kg_per_kg_ag:       3.0,
  emew_naoh_kg_per_kg_ag:       0.5,
  emew_kwh_per_kg_ag:           5.0,
  emew_operators:                2,
  // Block 4 — Copper Revenue
  include_copper:        'Yes',
  copper_recovery_pct:   90,
  cu_per_kg_ag:          0.1111,
  emew_cake_share_pct:   20,
  // Block 5 — CAPEX
  emew_capex_usd:          '',
  discount_rate_pct:       10,
  evaluation_period_years: 10,
  // Advanced — secondary reagents & process parameters
  h2o2_price_usd_kg:      0.55,
  h2so4_price_usd_kg:     0.20,
  di_water_price_usd_m3:  2.00,
  emew_power_method:       'Calibrated to UPMR actual',
  emew_load_factor:        0.486,
  extra_compressor_kw:     0,
  moebius_reagent_basis:   'As quoted',
  moebius_fixed_operators: 3,
};

// ── Component ────────────────────────────────────────────────────────────────
export default function MoebiusComparison() {
  const navigate = useNavigate();
  const [form, setForm]               = useState(INIT);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);
  const [pricesBadge, setPricesBadge] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = key => val => setForm(f => ({ ...f, [key]: val }));

  // Country auto-fill
  useEffect(() => {
    if (!form.country) return;
    const b = COUNTRY_BENCHMARKS[form.country];
    if (!b) return;
    setForm(f => ({
      ...f,
      power_price_usd_kwh: String(b.electricity),
      labour_cost_usd_yr:  String(b.labour),
    }));
  }, [form.country]); // eslint-disable-line

  // Live metal prices
  useEffect(() => {
    let cancelled = false;
    api.get('/metal-prices')
      .then(res => {
        if (cancelled) return;
        const d = res.data?.data || res.data;
        setForm(f => ({
          ...f,
          silver_price_usd_kg:         d.silver    != null ? String(Math.round(d.silver * TROY_OZ_PER_KG))    : f.silver_price_usd_kg,
          copper_price_usd_kg:         d.copper    != null ? String(Math.round(d.copper / 1000 * 100) / 100)  : f.copper_price_usd_kg,
          gold_price_usd_troy_oz:      d.gold      != null ? String(Math.round(d.gold))                       : f.gold_price_usd_troy_oz,
          palladium_price_usd_troy_oz: d.palladium != null ? String(Math.round(d.palladium))                  : f.palladium_price_usd_troy_oz,
          platinum_price_usd_troy_oz:  d.platinum  != null ? String(Math.round(d.platinum))                   : f.platinum_price_usd_troy_oz,
        }));
        setPricesBadge(d.fallback ? 'stale' : d.stale ? 'stale' : 'live');
      })
      .catch(() => { if (!cancelled) setPricesBadge('stale'); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Derived values
  const cap        = Number(form.net_ag_output_kg_d) || 600;
  const opDays     = Number(form.operating_days)     || 330;
  const annualAg   = cap * opDays;
  const agSpotTrOz = (Number(form.silver_price_usd_kg) / TROY_OZ_PER_KG).toFixed(2);
  const derivedCuPerAg = Number(form.feed_ag_pct) > 0
    ? (Number(form.feed_cu_pct) / Number(form.feed_ag_pct)).toFixed(4) : '—';

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.company_name.trim()) { setError('Company name is required.'); return; }
    if (!form.net_ag_output_kg_d || Number(form.net_ag_output_kg_d) <= 0) {
      setError('Saleable Ag output is required.'); return;
    }
    setSubmitting(true); setError(null);
    try {
      const res = await api.post('/assessment', {
        app_type:      'moebius_comparison',
        customer_name: form.company_name.trim(),
        inputs: { ...form, customer_name: form.company_name.trim() },
      });
      navigate(`/assessment/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 860, margin: '0 auto', padding: '1.5rem 0' }}>

      {/* Page header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{ width: 4, height: 32, background: `linear-gradient(${BLUE}, ${GREEN})`, borderRadius: 2 }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.375rem', color: DARK_BLUE }}>
              <span style={{ color: GREEN }}>e</span>mew vs Moebius — Value Comparison
            </h1>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#718096' }}>
              emew Rapid Silver + Copper Plate vs Moebius electrolytic refining
            </p>
          </div>
        </div>
      </div>

      {/* ── Customer ── */}
      <Block>
        <Field label="Company Name" required>
          <Txt value={form.company_name} onChange={set('company_name')}
            placeholder="e.g. Acme Refining Ltd." />
        </Field>
        <TwoCol>
          <Field label="Contact Person">
            <Txt value={form.contact_person} onChange={set('contact_person')}
              placeholder="e.g. Jane Smith" />
          </Field>
          <Field label="Country" hint="Auto-fills electricity and labour benchmarks.">
            <Sel value={form.country} onChange={set('country')}
              options={COUNTRIES} placeholder="— Select —" />
          </Field>
        </TwoCol>
      </Block>

      {/* ── Block 1: Feed & Production ── */}
      <Block>
        <BlockHeader number="1" title="Feed and Production Inputs"
          subtitle="Silver output target and feed material composition." />
        <TwoCol>
          <Field label="Saleable Ag Output" required unit="kg/d"
            hint="Net purified silver output per day.">
            <Num value={form.net_ag_output_kg_d} onChange={set('net_ag_output_kg_d')}
              unit="kg/d" min={50} max={2000} step={10} placeholder="e.g. 600" />
          </Field>
          <Field label="Existing Process"
            hint="Auto-filled. Compares emew against Moebius electrorefining.">
            <Txt value={form.existing_process} readOnly />
          </Field>
        </TwoCol>
        <Divider />
        <TwoCol>
          <Field label="Feed Ag" required unit="%"
            hint="Silver content of feed material (anode / grain / slimes).">
            <Num value={form.feed_ag_pct} onChange={set('feed_ag_pct')}
              unit="%" min={0} max={100} step={0.1} placeholder="e.g. 90" />
          </Field>
          <Field label="Feed Cu" required unit="%"
            hint="Copper content of feed. Drives emew copper cathode revenue.">
            <Num value={form.feed_cu_pct} onChange={set('feed_cu_pct')}
              unit="%" min={0} max={50} step={0.1} placeholder="e.g. 10" />
          </Field>
        </TwoCol>
        <Divider />
        <TwoCol>
          <Field label="Feed Au" unit="ppm" hint="Gold in feed — leave blank if absent.">
            <Num value={form.feed_au_ppm} onChange={set('feed_au_ppm')}
              unit="ppm" min={0} step={1} optional placeholder="e.g. 50" />
          </Field>
          <Field label="Feed Pd + Pt" unit="ppm" hint="Combined PGMs — leave blank if absent.">
            <Num value={form.feed_pdpt_ppm} onChange={set('feed_pdpt_ppm')}
              unit="ppm" min={0} step={1} optional placeholder="e.g. 10" />
          </Field>
        </TwoCol>
        <Divider />
        <div style={{ background: '#EBF0FA', borderRadius: 8, padding: '0.625rem 0.875rem',
          display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#718096' }}>Annual Ag output</div>
            <div style={{ fontWeight: 700, color: DARK_BLUE }}>{annualAg.toLocaleString()} kg/yr</div>
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#718096' }}>Cu/Ag ratio (from feed)</div>
            <div style={{ fontWeight: 600, color: DARK_BLUE }}>{derivedCuPerAg} kg Cu/kg Ag</div>
          </div>
        </div>
      </Block>

      {/* ── Block 2: Financial & Operational Inputs ── */}
      <Block>
        <BlockHeader number="2" title="Financial and Operational Inputs"
          subtitle="Metal prices load live. Electricity and labour auto-fill from country selection above." />

        {/* Metal Prices */}
        <SubLabel>Metal Prices</SubLabel>
        <ThreeCol>
          <Field label="Silver Price" unit="USD/kg"
            hint={`Live: USD/tr oz × 32.15. Spot ≈ $${agSpotTrOz}/tr oz`}>
            <Num value={form.silver_price_usd_kg} onChange={set('silver_price_usd_kg')}
              min={100} max={10000} step={1} badge={pricesBadge} />
          </Field>
          <Field label="Gold Price" unit="USD/tr oz"
            hint="Yahoo Finance (XAUUSD=X) spot">
            <Num value={form.gold_price_usd_troy_oz} onChange={set('gold_price_usd_troy_oz')}
              min={500} max={20000} step={1} badge={pricesBadge} />
          </Field>
          <Field label="Copper Price" unit="USD/kg"
            hint="Yahoo Finance HG=F (LME $/t ÷ 1,000)">
            <Num value={form.copper_price_usd_kg} onChange={set('copper_price_usd_kg')}
              min={1} max={30} step={0.01} badge={pricesBadge} />
          </Field>
        </ThreeCol>
        <TwoCol>
          <Field label="Palladium Price" unit="USD/tr oz"
            hint="Yahoo Finance (XPDUSD=X) spot">
            <Num value={form.palladium_price_usd_troy_oz} onChange={set('palladium_price_usd_troy_oz')}
              min={100} max={10000} step={1} badge={pricesBadge} />
          </Field>
          <Field label="Platinum Price" unit="USD/tr oz"
            hint="Yahoo Finance (XPTUSD=X) spot">
            <Num value={form.platinum_price_usd_troy_oz} onChange={set('platinum_price_usd_troy_oz')}
              min={100} max={10000} step={1} badge={pricesBadge} />
          </Field>
        </TwoCol>

        <Divider />

        {/* Operating & Financial */}
        <SubLabel>Operating &amp; Financial</SubLabel>
        <ThreeCol>
          <Field label="Silver Lease Rate" unit="% / yr"
            hint="Annual financing cost on silver inventory. Typical: 4–15%.">
            <Num value={form.silver_lease_rate * 100}
              onChange={v => set('silver_lease_rate')(v / 100)}
              unit="%" min={0} max={30} step={0.5} />
          </Field>
          <Field label="Electricity Cost" unit="USD/kWh"
            hint={form.country && COUNTRY_BENCHMARKS[form.country]
              ? `IEA regional rate — ${form.country}`
              : 'IEA global industrial avg'}>
            <Num value={form.power_price_usd_kwh} onChange={set('power_price_usd_kwh')}
              unit="$/kWh" min={0.01} max={1} step={0.001} />
          </Field>
          <Field label="Labour Cost" unit="USD/yr"
            hint={form.country && COUNTRY_BENCHMARKS[form.country]
              ? `ILO/World Bank — ${form.country}`
              : 'ILO/World Bank global benchmark'}>
            <Num value={form.labour_cost_usd_yr} onChange={set('labour_cost_usd_yr')}
              unit="$/yr" min={5000} max={200000} step={1000} />
          </Field>
        </ThreeCol>
        <TwoCol>
          <Field label="Operating Days" unit="days / yr">
            <Num value={form.operating_days} onChange={set('operating_days')}
              unit="days" min={200} max={365} step={1} />
          </Field>
          <Field label="Target Payback" unit="years"
            hint="Customer's payback expectation for emew capital.">
            <Num value={form.target_payback_years} onChange={set('target_payback_years')}
              unit="yr" min={0.5} max={10} step={0.5} />
          </Field>
        </TwoCol>

        <Divider />

        {/* Reagent Prices */}
        <SubLabel>Reagent Prices</SubLabel>
        <ThreeCol>
          <Field label="Nitric Acid (HNO₃)" unit="USD/kg"
            hint="Industrial grade 60% delivered">
            <Num value={form.hno3_price_usd_kg} onChange={set('hno3_price_usd_kg')}
              unit="$/kg" min={0.1} max={5} step={0.01} />
          </Field>
          <Field label="Caustic Soda (NaOH)" unit="USD/kg"
            hint="50% solution delivered price">
            <Num value={form.naoh_price_usd_kg} onChange={set('naoh_price_usd_kg')}
              unit="$/kg" min={0.1} max={5} step={0.01} />
          </Field>
          <Field label="Hydrochloric Acid (HCl)" unit="USD/L"
            hint="30–33% industrial grade delivered">
            <Num value={form.hcl_price_usd_l} onChange={set('hcl_price_usd_l')}
              unit="$/L" min={0.01} max={2} step={0.01} />
          </Field>
        </ThreeCol>
        <TwoCol>
          <Field label="Zinc Dust / Powder" unit="USD/kg"
            hint="Cementation grade — enter 0 if not used">
            <Num value={form.zinc_dust_usd_kg} onChange={set('zinc_dust_usd_kg')}
              unit="$/kg" min={0} max={20} step={0.1} optional />
          </Field>
          <Field label="Cu Sludge Disposal" unit="USD/t wet"
            hint="Copper hydroxide sludge — emew generates ~20% of Moebius volume">
            <Num value={form.sludge_disposal_usd_t} onChange={set('sludge_disposal_usd_t')}
              unit="$/t" min={0} max={2000} step={10} />
          </Field>
        </TwoCol>
      </Block>

      {/* ── Block 3: Process Benchmarks ── */}
      <Block>
        <BlockHeader number="3" title="Process Benchmarks"
          subtitle="Industry defaults pre-filled — override to match the customer's actual process data." />

        {/* ── Moebius sub-section ── */}
        <div style={{ background: '#F7F8FA', borderRadius: 8, padding: '0.5rem 0.875rem',
          marginBottom: '1rem', borderLeft: '3px solid #718096' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4A5568',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Moebius Electrorefining
          </span>
        </div>

        <TwoCol>
          <Field label="Ag Inventory" unit="\u00d7 daily Ag output"
            hint="Silver locked in anodes + electrolyte. Data sheet: typically 6\u201310\u00d7.">
            <Num value={form.moebius_ag_inventory_factor} onChange={set('moebius_ag_inventory_factor')}
              min={1} max={20} step={0.1} />
          </Field>
          <Field label="Electricity" unit="kWh / kg Ag"
            hint="Rectifier + auxiliaries + fume scrubber. Typical: 1\u20133 kWh/kg Ag.">
            <Num value={form.moebius_kwh_per_kg_ag} onChange={set('moebius_kwh_per_kg_ag')}
              min={0} max={10} step={0.1} />
          </Field>
        </TwoCol>

        <ThreeCol>
          <Field label="Nitric Acid (HNO\u2083)" unit="kg / kg Ag"
            hint="Anode passivation removal and electrolyte make-up">
            <Num value={form.moebius_hno3_kg_per_kg_ag} onChange={set('moebius_hno3_kg_per_kg_ag')}
              min={0} max={10} step={0.1} />
          </Field>
          <Field label="Hydrochloric Acid (HCl)" unit="L / kg Ag"
            hint="Anode cleaning and equipment passivation">
            <Num value={form.moebius_hcl_l_per_kg_ag} onChange={set('moebius_hcl_l_per_kg_ag')}
              min={0} max={5} step={0.05} />
          </Field>
          <Field label="Caustic Soda (NaOH)" unit="kg / kg Ag"
            hint="Cu(OH)\u2082 precipitation in waste treatment">
            <Num value={form.moebius_naoh_kg_per_kg_ag} onChange={set('moebius_naoh_kg_per_kg_ag')}
              min={0} max={5} step={0.1} />
          </Field>
        </ThreeCol>

        <TwoCol>
          <Field label="Zinc Powder" unit="kg / kg Ag"
            hint="Cementation grade \u2014 enter 0 if not used">
            <Num value={form.moebius_zn_dust_kg_per_kg_ag} onChange={set('moebius_zn_dust_kg_per_kg_ag')}
              min={0} max={2} step={0.01} />
          </Field>
          <Field label="Operators" unit="count"
            hint="FTEs dedicated to Moebius refining (excl. shared services)">
            <Num value={form.moebius_operators} onChange={set('moebius_operators')}
              min={0} max={20} step={1} />
          </Field>
        </TwoCol>

        <Divider />

        {/* ── emew sub-section ── */}
        <div style={{ background: '#F0FFF4', borderRadius: 8, padding: '0.5rem 0.875rem',
          marginBottom: '1rem', borderLeft: '3px solid #38D430' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#276749',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            emew Rapid Silver Plate
          </span>
        </div>

        <TwoCol>
          <Field label="Ag Inventory" unit="\u00d7 daily Ag output"
            hint="Silver in emew circuit \u2014 typically 2\u20133\u00d7 daily output.">
            <Num value={form.emew_ag_inventory_multiple} onChange={set('emew_ag_inventory_multiple')}
              min={0.5} max={8} step={0.1} />
          </Field>
          <Field label="Electricity" unit="kWh / kg Ag"
            hint="Calibrated to UPMR actual: ~5 kWh/kg Ag. Adjust for site configuration.">
            <Num value={form.emew_kwh_per_kg_ag} onChange={set('emew_kwh_per_kg_ag')}
              min={0} max={20} step={0.1} />
          </Field>
        </TwoCol>

        <ThreeCol>
          <Field label="Nitric Acid (HNO\u2083)" unit="kg / kg Ag"
            hint="Dissolution of silver grain / anode material">
            <Num value={form.emew_hno3_kg_per_kg_ag} onChange={set('emew_hno3_kg_per_kg_ag')}
              min={0} max={10} step={0.1} />
          </Field>
          <Field label="Caustic Soda (NaOH)" unit="kg / kg Ag"
            hint="Neutralisation and Cu(OH)\u2082 removal">
            <Num value={form.emew_naoh_kg_per_kg_ag} onChange={set('emew_naoh_kg_per_kg_ag')}
              min={0} max={5} step={0.1} />
          </Field>
          <Field label="Operators" unit="count"
            hint="FTEs dedicated to emew operation">
            <Num value={form.emew_operators} onChange={set('emew_operators')}
              min={0} max={10} step={1} />
          </Field>
        </ThreeCol>

        <Divider />

        {/* ── Live comparison table ── */}
        {(() => {
          const slr   = Number(form.silver_lease_rate);
          const sp    = Number(form.silver_price_usd_kg);
          const ep    = Number(form.power_price_usd_kwh);
          const lc    = Number(form.labour_cost_usd_yr);
          const hno3p = Number(form.hno3_price_usd_kg);
          const hclp  = Number(form.hcl_price_usd_l);
          const naohp = Number(form.naoh_price_usd_kg);
          const znp   = Number(form.zinc_dust_usd_kg);

          const mInv  = Number(form.moebius_ag_inventory_factor) * cap * sp * slr;
          const mElec = Number(form.moebius_kwh_per_kg_ag) * annualAg * ep;
          const mReag = (Number(form.moebius_hno3_kg_per_kg_ag)    * hno3p
                       + Number(form.moebius_hcl_l_per_kg_ag)      * hclp
                       + Number(form.moebius_naoh_kg_per_kg_ag)    * naohp
                       + Number(form.moebius_zn_dust_kg_per_kg_ag) * znp) * annualAg;
          const mLab  = Number(form.moebius_operators) * lc;

          const eInv  = Number(form.emew_ag_inventory_multiple) * cap * sp * slr;
          const eElec = Number(form.emew_kwh_per_kg_ag) * annualAg * ep;
          const eReag = (Number(form.emew_hno3_kg_per_kg_ag)  * hno3p
                       + Number(form.emew_naoh_kg_per_kg_ag)  * naohp) * annualAg;
          const eLab  = Number(form.emew_operators) * lc;

          const totalAdv = (mInv + mElec + mReag + mLab) - (eInv + eElec + eReag + eLab);
          const fmt  = v => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
          const fmtD = v => (v >= 0 ? '+' : '') + fmt(v);
          const rows = [
            { label: 'Ag inventory lease', m: mInv,  e: eInv  },
            { label: 'Electricity',         m: mElec, e: eElec },
            { label: 'Reagents',            m: mReag, e: eReag },
            { label: 'Labour',              m: mLab,  e: eLab  },
          ];
          const hdrCell = { padding: '0.35rem 0.625rem', fontSize: '0.7rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff' };
          const cell = { padding: '0.35rem 0.625rem', fontSize: '0.8rem' };
          return (
            <div style={{ marginTop: '0.75rem', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
                background: DARK_BLUE }}>
                <div style={hdrCell}>Cost Item</div>
                <div style={{ ...hdrCell, textAlign: 'right' }}>Moebius / yr</div>
                <div style={{ ...hdrCell, textAlign: 'right' }}>emew / yr</div>
                <div style={{ ...hdrCell, textAlign: 'right' }}>emew Advantage</div>
              </div>
              {rows.map((r, i) => (
                <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
                  background: i % 2 === 0 ? '#fff' : '#F7F8FA',
                  borderBottom: '1px solid #EDF2F7' }}>
                  <div style={{ ...cell, color: '#4A5568' }}>{r.label}</div>
                  <div style={{ ...cell, textAlign: 'right', color: DARK_BLUE }}>{fmt(r.m)}</div>
                  <div style={{ ...cell, textAlign: 'right', color: DARK_BLUE }}>{fmt(r.e)}</div>
                  <div style={{ ...cell, textAlign: 'right', fontWeight: 600,
                    color: r.m - r.e >= 0 ? '#276749' : '#C53030' }}>{fmtD(r.m - r.e)}</div>
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
                background: totalAdv >= 0 ? '#F0FFF4' : '#FFF5F5',
                borderTop: `2px solid ${BORDER}` }}>
                <div style={{ ...cell, fontWeight: 700, color: DARK_BLUE }}>Total (benchmarks only)</div>
                <div style={cell} />
                <div style={cell} />
                <div style={{ ...cell, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem',
                  color: totalAdv >= 0 ? '#276749' : '#C53030' }}>{fmtD(totalAdv)}/yr</div>
              </div>
            </div>
          );
        })()}
      </Block>

      {/* ── Block 4: Copper Revenue ── */}
      <Block>
        <BlockHeader number="4" title="Copper Revenue"
          subtitle="emew co-produces copper cathode — a direct revenue credit against OpEx." />
        <div style={{ marginBottom: '1rem' }}>
          <Field label="Include Copper Revenue?">
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {['Yes', 'No'].map(opt => (
                <button key={opt} type="button" onClick={() => set('include_copper')(opt)}
                  style={{
                    flex: 1, padding: '0.5rem', borderRadius: 6, cursor: 'pointer',
                    fontWeight: 600, fontSize: '0.9375rem',
                    border: `1.5px solid ${form.include_copper === opt ? BLUE : BORDER}`,
                    background: form.include_copper === opt ? '#EBF0FA' : '#fff',
                    color: form.include_copper === opt ? DARK_BLUE : '#718096',
                  }}>{opt}</button>
              ))}
            </div>
          </Field>
        </div>
        {form.include_copper === 'Yes' && (
          <>
            <TwoCol>
              <Field label="Copper Recovery to Cathode" unit="%"
                hint="Share of dissolved Cu plated as saleable cathode.">
                <Num value={form.copper_recovery_pct} onChange={set('copper_recovery_pct')}
                  unit="%" min={50} max={100} step={1} />
              </Field>
              <Field label="Cu Dissolved per kg Ag Output" unit="kg Cu / kg Ag"
                hint={`From feed: ${derivedCuPerAg} kg/kg. Override if known.`}>
                <Num value={form.cu_per_kg_ag} onChange={set('cu_per_kg_ag')}
                  min={0} max={0.5} step={0.001} />
              </Field>
            </TwoCol>
            <Divider />
            <TwoCol>
              <Field label="emew Cake as % of Moebius Cake" unit="%"
                hint="Default 20% — emew electrowin Cu avoids most hydroxide cake.">
                <Num value={form.emew_cake_share_pct} onChange={set('emew_cake_share_pct')}
                  unit="%" min={5} max={100} step={1} />
              </Field>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                <div style={{ background: '#F0FFF4', borderRadius: 8, padding: '0.625rem 0.875rem', width: '100%' }}>
                  <div style={{ fontSize: '0.7rem', color: '#276749' }}>Est. copper cathode revenue</div>
                  <div style={{ fontWeight: 700, color: '#276749' }}>
                    {(annualAg * Number(form.cu_per_kg_ag) *
                      (Number(form.copper_recovery_pct) / 100) *
                      Number(form.copper_price_usd_kg)).toLocaleString('en-US',
                      { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}/yr
                  </div>
                </div>
              </div>
            </TwoCol>
          </>
        )}
      </Block>

      {/* ── Block 5: CAPEX ── */}
      <Block>
        <BlockHeader number="5" title="CAPEX"
          subtitle="Used to compute the maximum emew CAPEX premium and present value of savings." />
        <TwoCol>
          <Field label="emew System CAPEX" unit="USD"
            hint="Leave blank to compute the maximum CAPEX ceiling from savings.">
            <Num value={form.emew_capex_usd} onChange={set('emew_capex_usd')}
              min={0} step={10000} optional />
          </Field>
          <TwoCol>
            <Field label="Discount Rate" unit="% / yr"
              hint="Customer WACC or hurdle rate.">
              <Num value={form.discount_rate_pct} onChange={set('discount_rate_pct')}
                unit="%" min={1} max={50} step={0.5} />
            </Field>
            <Field label="Evaluation Period" unit="years">
              <Num value={form.evaluation_period_years} onChange={set('evaluation_period_years')}
                unit="yr" min={1} max={20} step={1} />
            </Field>
          </TwoCol>
        </TwoCol>
      </Block>

      {/* ── Advanced ── */}
      <div style={{ marginBottom: '1.25rem' }}>
        <button type="button" onClick={() => setShowAdvanced(v => !v)}
          style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 8,
            padding: '0.5rem 1rem', cursor: 'pointer', width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            color: '#718096', fontSize: '0.875rem', fontWeight: 500 }}>
          <span>Advanced — Secondary Reagents &amp; Process Benchmarks</span>
          <span>{showAdvanced ? 'Hide' : 'Show'}</span>
        </button>
        {showAdvanced && (
          <Block style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <BlockHeader number="A" title="Secondary Reagents" />
            <ThreeCol>
              <Field label="H₂O₂ 30%" unit="USD/kg">
                <Num value={form.h2o2_price_usd_kg} onChange={set('h2o2_price_usd_kg')}
                  unit="$/kg" min={0.1} max={5} step={0.01} />
              </Field>
              <Field label="H₂SO₄ 98%" unit="USD/kg">
                <Num value={form.h2so4_price_usd_kg} onChange={set('h2so4_price_usd_kg')}
                  unit="$/kg" min={0.01} max={2} step={0.01} />
              </Field>
              <Field label="DI Water" unit="USD/m³">
                <Num value={form.di_water_price_usd_m3} onChange={set('di_water_price_usd_m3')}
                  unit="$/m³" min={0} max={50} step={0.1} />
              </Field>
            </ThreeCol>
            <Divider />
            <BlockHeader number="B" title="emew Power Benchmarks" />
            <TwoCol>
              <Field label="Power Method">
                <Sel value={form.emew_power_method} onChange={set('emew_power_method')} options={[
                  'Calibrated to UPMR actual',
                  'Flat kWh/kg (UPMR actual)',
                  'Load factor input',
                ]} />
              </Field>
              {form.emew_power_method === 'Load factor input' && (
                <Field label="emew Load Factor" unit="0–1"
                  hint="Fraction of connected load running. UPMR actual 0.486.">
                  <Num value={form.emew_load_factor} onChange={set('emew_load_factor')}
                    min={0.1} max={1} step={0.01} />
                </Field>
              )}
            </TwoCol>
            <Field label="Extra Compressor Load" unit="kW"
              hint="Add ~25 kW if instrument air compressor is not in the connected load.">
              <Num value={form.extra_compressor_kw} onChange={set('extra_compressor_kw')}
                min={0} max={200} step={5} />
            </Field>
            <Divider />
            <BlockHeader number="C" title="Moebius Reference Basis" />
            <TwoCol>
              <Field label="Moebius Reagent Basis">
                <Sel value={form.moebius_reagent_basis} onChange={set('moebius_reagent_basis')} options={[
                  { value: 'As quoted', label: 'As quoted (interpolated 150/300/600 kg/d)' },
                  { value: 'Normalised to 600', label: 'Normalised to 600 kg/d rate' },
                ]} />
              </Field>
              <Field label="Moebius Fixed Operators" unit="#"
                hint="Receiving (2) + waste treatment (1) = 3 fixed.">
                <Num value={form.moebius_fixed_operators} onChange={set('moebius_fixed_operators')}
                  min={0} max={10} step={1} />
              </Field>
            </TwoCol>
          </Block>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem', color: '#C53030', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* Submit */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '0.5rem' }}>
        <button type="button" onClick={() => window.history.back()}
          style={{ padding: '0.625rem 1.5rem', borderRadius: 8, border: `1.5px solid ${BORDER}`,
            background: '#fff', color: '#4A5568', fontWeight: 600, cursor: 'pointer', fontSize: '0.9375rem' }}>
          Cancel
        </button>
        <button type="submit" disabled={submitting}
          style={{ padding: '0.625rem 2rem', borderRadius: 8, border: 'none',
            background: submitting ? '#A0AEC0' : `linear-gradient(135deg, ${BLUE}, #2A4F9E)`,
            color: '#fff', fontWeight: 700, fontSize: '0.9375rem',
            cursor: submitting ? 'default' : 'pointer', transition: 'background 0.2s' }}>
          {submitting ? 'Running Analysis...' : 'Run Analysis'}
        </button>
      </div>
    </form>
  );
}
